import { useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
} from "framer-motion";
import { ArrowRight } from "lucide-react";
import WhatsAppIcon from "../WhatsAppIcon";

const WA_URL =
  "https://wa.me/524424319100?text=Hola,%20necesito%20un%20diagn%C3%B3stico%20urgente%20para%20la%20guarda%20telesc%C3%B3pica%20de%20mi%20CNC";
const HERO_IMG =
  "https://customer-assets-wrfwihn1.emergentagent.net/job_industrial-shields/artifacts/e72457e01eb8eba9_FB_IMG_1783650592744.jpg";

const lineReveal = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { delay: 0.25 + i * 0.14, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  }),
};

function MaskedLine({ index, className, children }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        custom={index}
        variants={lineReveal}
        initial="hidden"
        animate="visible"
        className={`block ${className}`}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function RepairHero({ onNavigate }) {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const glowY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  // 3D tilt on the clipped product frame
  const frameRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), {
    stiffness: 120,
    damping: 18,
  });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), {
    stiffness: 120,
    damping: 18,
  });
  const onMouseMove = (e) => {
    const r = frameRef.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onMouseLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section
      ref={sectionRef}
      data-testid="repair-hero-section"
      className="relative min-h-screen flex items-center overflow-hidden pt-[72px]"
    >
      {/* Backdrop: grid + parallax cyan glow + giant outline word */}
      <div className="absolute inset-0 z-0">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(rgba(148,163,184,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.07) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
        <motion.div
          style={{ y: glowY }}
          className="absolute -top-40 right-[-10%] w-[55vw] h-[55vw] rounded-full bg-[#00B4D8]/10 blur-[120px]"
        />
        <div className="absolute bottom-0 left-0 font-display font-black text-[22vw] leading-none text-transparent select-none pointer-events-none gf-text-outline opacity-40">
          CNC
        </div>
      </div>

      {/* Side rail */}
      <div className="hidden lg:flex absolute left-10 top-1/2 -translate-y-1/2 flex-col gap-6 z-10">
        <div className="w-px h-24 bg-gradient-to-b from-transparent via-[#00B4D8] to-transparent" />
        <span className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-white/60 -rotate-90 origin-center whitespace-nowrap">
          URGENCIAS · 24/7
        </span>
        <div className="w-px h-24 bg-gradient-to-b from-[#00B4D8] via-transparent to-transparent" />
      </div>

      <motion.div
        style={{ y: contentY }}
        className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-24 w-full grid lg:grid-cols-12 gap-14 items-center"
      >
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="w-12 h-px bg-[#00B4D8]" />
            <span className="gf-overline">Servicio urgente de reparación</span>
          </motion.div>

          <h1
            data-testid="repair-hero-title"
            className="font-display font-black tracking-tighter leading-[1.02] text-white"
          >
            <MaskedLine index={0} className="text-4xl sm:text-5xl lg:text-6xl">
              Reparación y Reconstrucción
            </MaskedLine>
            <MaskedLine index={1} className="text-4xl sm:text-5xl lg:text-6xl text-[#00B4D8]">
              de Guardas Telescópicas
            </MaskedLine>
            <MaskedLine
              index={2}
              className="mt-5 text-lg sm:text-xl lg:text-2xl font-light text-white/75 tracking-normal leading-snug max-w-2xl"
            >
              para Centros de Maquinado CNC en Querétaro.
            </MaskedLine>
          </h1>

          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.7 }}
            className="mt-8 text-base md:text-lg text-white/60 max-w-xl leading-relaxed font-normal"
          >
            Diagnóstico el mismo día. Wipers, lamelas y guías restauradas con
            garantía por escrito — sin detener tu producción más de lo
            necesario.
          </motion.h2>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <a
              href={WA_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-whatsapp-urgente"
              className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1eb85a] text-white px-8 py-4 text-sm font-semibold uppercase tracking-[0.1em] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-10px_rgba(37,211,102,0.5)]"
              style={{ borderRadius: "2px" }}
            >
              <WhatsAppIcon size={24} className="text-white shrink-0" />
              Solicitar diagnóstico urgente por WhatsApp
            </a>
            <button
              onClick={() => onNavigate("cotizar")}
              data-testid="cta-cotizar-reparacion"
              className="gf-btn-ghost-inverse justify-center"
            >
              Cotizar reparación
              <ArrowRight size={17} />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-14 flex flex-wrap gap-x-10 gap-y-4 font-mono-accent text-[11px] uppercase tracking-[0.2em] text-white/50"
          >
            <span data-testid="hero-trust-1">· Respuesta 24/7</span>
            <span data-testid="hero-trust-2">· Diagnóstico el mismo día</span>
            <span data-testid="hero-trust-3">· Garantía por escrito</span>
          </motion.div>
        </div>

        {/* Clipped product frame with 3D tilt */}
        <div className="lg:col-span-5 hidden lg:block" style={{ perspective: "1200px" }}>
          <motion.div
            ref={frameRef}
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
            style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
            data-testid="repair-hero-frame"
          >
            <div className="absolute -inset-8 bg-[#00B4D8]/15 blur-3xl rounded-full" />
            <div className="relative border border-white/15 bg-[#0A1B35]/60 backdrop-blur-sm p-3" style={{ borderRadius: "2px" }}>
              <div className="relative overflow-hidden" style={{ borderRadius: "2px" }}>
                <img
                  src={HERO_IMG}
                  alt="Técnico de Guarda Flex ensamblando una guarda telescópica en el taller"
                  className="w-full aspect-[4/3] object-cover object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#061226]/70 via-transparent to-transparent" />
              </div>
              <div className="flex items-center justify-between mt-3 px-1">
                <span className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#00B4D8]">
                  Guarda telescópica · Trabajo real en taller
                </span>
                <span className="font-mono-accent text-[10px] text-white/40">GF-RP/01</span>
              </div>
              <span className="gf-corner border-t-2 border-l-2 -top-px -left-px" />
              <span className="gf-corner border-b-2 border-r-2 -bottom-px -right-px" />
            </div>
          </motion.div>
        </div>
      </motion.div>

      <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#00B4D8]/60 to-transparent z-10" />
    </section>
  );
}
